import React from "react";
import {
  AppBar as MuiAppBar,
  Box,
  Toolbar,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ViewName } from "../../../types";
import LanguageSelector from "../../LanguageSelector";
import { DISABLE_RTL_PROPS, DISABLE_RTL_STYLES } from "../../../utils/rtlUtils";
import { Menu } from "./Menu";

interface AppBarProps {
  showBackButton: boolean;
  currentView: ViewName;
  role: 1 | 2 | null;
  userId: string | null;
  isRTL: boolean;
  getTranslation: (key: string) => string;
  handleBack: () => void;
  anchorEl: HTMLElement | null;
  open: boolean;
  languageMenuOpen: boolean;
  handleMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
  handleMenuClose: () => void;
  handleLanguageMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
  handleLanguageMenuClose: () => void;
  handleLanguageChange: () => void;
  handleSignOut: () => void;
  setCurrentView: (view: ViewName) => void;
}

export const AppBar: React.FC<AppBarProps> = ({
  showBackButton,
  currentView,
  role,
  userId,
  isRTL,
  getTranslation,
  handleBack,
  anchorEl,
  open,
  languageMenuOpen,
  handleMenuClick,
  handleMenuClose,
  handleLanguageMenuClick,
  handleLanguageMenuClose,
  handleLanguageChange,
  handleSignOut,
  setCurrentView,
}) => {
  return (
    <MuiAppBar
      position="fixed"
      sx={{
        background: "transparent",
        boxShadow: "none",
        zIndex: 10,
      }}
    >
      <Toolbar
        {...DISABLE_RTL_PROPS}
        sx={{
          ...DISABLE_RTL_STYLES,
          justifyContent: "space-between",
          alignItems: "center",
          gap: { xs: 1.5, md: 2 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {showBackButton && (
            <IconButton
              size="large"
              edge="start"
              aria-label="back"
              sx={{ mr: 2, color: "black" }}
              onClick={handleBack}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 1.5, md: 2 },
          }}
        >
          {/* Language dropdown for signup/contact views */}
          {(currentView === "signup" ||
            currentView === "contact" ||
            currentView === "forgot-password") && (
            <LanguageSelector variant="landing" />
          )}

          {/* Menu button - show based on conditions */}
          {([
            "user-info",
            "edit-profile",
            "scores",
            "recommendations",
            "feedback",
            "UserDetails",
            "MoreUserDetails",
            "Welcome",
            "distributor-welcome",
            "DistributorUserDetails",
            "DistributorDeactivatedUser",
          ].includes(currentView) &&
            (role === 1 || userId)) && (
            <Menu
              currentView={currentView}
              role={role}
              userId={userId}
              isRTL={isRTL}
              getTranslation={getTranslation}
              anchorEl={anchorEl}
              open={open}
              languageMenuOpen={languageMenuOpen}
              handleMenuClick={handleMenuClick}
              handleMenuClose={handleMenuClose}
              handleLanguageMenuClick={handleLanguageMenuClick}
              handleLanguageMenuClose={handleLanguageMenuClose}
              handleLanguageChange={handleLanguageChange}
              handleSignOut={handleSignOut}
              setCurrentView={setCurrentView}
            />
          )}
        </Box>
      </Toolbar>
    </MuiAppBar>
  );
};
